import { inject, Injectable } from '@angular/core';
import { FileData } from '@core/models/file-data';
import { FileObject } from '@core/models/file-object';
import { UtilityService } from '@core/services/utility.service';
import { Observable, Observer, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class FileUtilityService {
  private readonly utilityService = inject(UtilityService);

  public readonly ALLOWED_EXTENSIONS: readonly string[] = [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.gif',
    '.tif',
    '.tiff',
    '.png',
    '.bmp',
    '.doc',
    '.docx',
    '.xls',
    '.xlsx',
    '.ppt',
    '.pps',
    '.pptx',
    '.ppsx',
    '.sldx',
    '.heic',
    '.heif',
  ] as const;

  public getFileDataForFiles(files: File[]): Observable<FileData[]> {
    const fileObservables = files.map(file => this.getFileDataForFile(file));
    return forkJoin(fileObservables);
  }

  public getFileDataForFile(file: File): Observable<FileData> {
    return this.readFileContent(file).pipe(
      map(fileData => {
        return this.parseFileReaderResult(fileData);
      }),
      map(fileData => ({
        name: this.utilityService.formatAttachmentName(file.name),
        data: fileData,
      }))
    );
  }

  private parseFileReaderResult(result: string): string {
    const data = result.split(',');
    if (data.length > 1) {
      // file data after file details
      return data[1];
    } else {
      return result;
    }
  }

  private readFileContent(file: File): Observable<string> {
    return new Observable((observer: Observer<string>) => {
      const reader = new FileReader();

      reader.onload = (): void => {
        observer.next(reader.result as string);
        observer.complete();
      };

      reader.onerror = (error): void => {
        observer.error(error);
      };

      reader.readAsDataURL(file);
    });
  }

  public readFile(file, call): void {
    const reader = new FileReader();
    reader.onload = function (e): void {
      return call(e, file.name);
    };
    reader.readAsDataURL(file);
  }

  public onFileSelected(files: File[]): FileObject {
    if (!files || files.length === 0) {
      return {};
    }
    const file = files[0];

    // Ignore empty files
    if (file.size === 0) {
      return {};
    }

    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!this.ALLOWED_EXTENSIONS.includes(ext)) {
      return {};
    }

    // Valid file
    return {
      File: file,
      Filename: file.name,
      Filedata: file,
    };
  }
}
